<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { Head, router } from '@inertiajs/vue3'
import DashboardLayout from '@/Layouts/DashboardLayout.vue'
import ConversationList from '@/Components/Chat/ConversationList.vue'
import MessageThread from '@/Components/Chat/MessageThread.vue'
import MessageInput from '@/Components/Chat/MessageInput.vue'
import MembersPanel from '@/Components/Chat/MembersPanel.vue'
import { MessageSquare, ArrowLeft } from 'lucide-vue-next'
import axios from 'axios'

interface Message {
  id: number
  user_id: number
  sender_name: string
  sender_avatar: string | null
  body: string
  created_at: string
  created_at_iso: string
  is_own: boolean
}

interface ConversationDetail {
  id: number
  type: string
  title: string
  teacher: { id: number; name: string; avatar_url: string | null }
  subject: { id: number; name: string } | null
  level: { id: number; name: string } | null
  participants: Array<{ id: number; name: string; avatar_url: string | null; role: string }>
}

interface Conversation {
  id: number
  type: string
  title: string
  last_message: { body: string; sender_name: string; sent_at: string } | null
  unread_count: number
  participant_count: number
  teacher: { id: number; name: string; avatar_url: string | null }
  subject: { id: number; name: string; color: string | null } | null
  level: { id: number; name: string } | null
}

const props = defineProps<{
  conversations: Conversation[]
  activeConversation: ConversationDetail | null
  messages: Message[] | null
  hasMoreMessages?: boolean
}>()

const localMessages = ref<Message[]>(props.messages ?? [])
const hasMore = ref(props.hasMoreMessages ?? false)
const loadingOlder = ref(false)
const sending = ref(false)
const showThread = ref(!!props.activeConversation)

const activeConversationId = computed(() => props.activeConversation?.id ?? null)
const isGroupConversation = computed(() => props.activeConversation?.type === 'group')

const POLL_INTERVAL = 20_000
let pollTimer: ReturnType<typeof setInterval> | null = null

watch(() => props.messages, (newMessages) => {
  localMessages.value = newMessages ?? []
}, { immediate: true })

watch(() => props.hasMoreMessages, (val) => {
  hasMore.value = val ?? false
}, { immediate: true })

const selectConversation = (conversationId: number) => {
  showThread.value = true
  router.get(`/teacher/messages/${conversationId}`, {}, {
    preserveState: false,
    preserveScroll: false,
  })
}

const goBackToList = () => {
  showThread.value = false
  router.get('/teacher/messages', {}, {
    preserveState: false,
  })
}

const loadOlderMessages = async () => {
  if (!activeConversationId.value || loadingOlder.value || !hasMore.value) return
  if (localMessages.value.length === 0) return

  loadingOlder.value = true
  const oldestId = localMessages.value[0].id

  try {
    const { data } = await axios.get(`/teacher/messages/${activeConversationId.value}/older`, {
      params: { before: oldestId }
    })
    if (data.messages && data.messages.length > 0) {
      localMessages.value.unshift(...data.messages)
    }
    hasMore.value = data.has_more
  } catch (e) {
    // Silently fail
  } finally {
    loadingOlder.value = false
  }
}

const sendMessage = async (body: string) => {
  if (!activeConversationId.value || sending.value) return

  sending.value = true
  try {
    const { data } = await axios.post(`/teacher/messages/${activeConversationId.value}`, { body }, {
      headers: { 'Accept': 'application/json' }
    })
    localMessages.value.push(data)
  } catch (e: any) {
    console.error('Failed to send message', e)
  } finally {
    sending.value = false
  }
}

const poll = async () => {
  if (!activeConversationId.value) return

  const lastId = localMessages.value.length > 0
    ? localMessages.value[localMessages.value.length - 1].id
    : 0

  try {
    const { data } = await axios.get(`/teacher/messages/${activeConversationId.value}/poll`, {
      params: { after: lastId }
    })
    if (data.messages && data.messages.length > 0) {
      localMessages.value.push(...data.messages)
    }
  } catch (e) {
    // Silently fail on poll errors
  }
}

const startPolling = () => {
  if (pollTimer) return
  pollTimer = setInterval(poll, POLL_INTERVAL)
}

const stopPolling = () => {
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = null
  }
}

onMounted(() => {
  if (activeConversationId.value) {
    startPolling()
  }
})

onUnmounted(stopPolling)

watch(activeConversationId, (newId) => {
  stopPolling()
  if (newId) {
    startPolling()
  }
})
</script>

<template>
  <Head title="Messages" />
  <DashboardLayout>
    <template #flush>
    <div class="h-full flex">
      <!-- Conversation List (left panel) -->
      <div
        class="w-full md:w-80 lg:w-96 border-r border-gray-200 bg-white shrink-0"
        :class="{ 'hidden md:flex md:flex-col': showThread && activeConversation }"
      >
        <ConversationList
          :conversations="conversations"
          :active-conversation-id="activeConversationId"
          empty-message="Conversations will appear here when students subscribe to your Silver or Gold plans"
          @select="selectConversation"
        />
      </div>

      <!-- Thread Panel (center) -->
      <div
        class="flex-1 flex flex-col bg-white min-h-0"
        :class="{ 'hidden md:flex': !showThread && !activeConversation }"
      >
        <!-- Thread header -->
        <div v-if="activeConversation" class="flex items-center px-4 py-3 border-b border-gray-200 shrink-0">
          <button
            class="md:hidden p-1 -ml-1 mr-3 text-gray-500 hover:text-gray-700"
            @click="goBackToList"
          >
            <ArrowLeft class="w-5 h-5" />
          </button>
          <div>
            <h3 class="text-sm font-semibold text-gray-900">{{ activeConversation.title }}</h3>
            <p v-if="isGroupConversation" class="text-xs text-gray-500">
              {{ activeConversation.participants.length }} members
            </p>
            <p v-else class="text-xs text-gray-500">
              Direct message
            </p>
          </div>
        </div>

        <!-- Messages -->
        <MessageThread
          v-if="activeConversation"
          :messages="localMessages"
          :has-more="hasMore"
          :loading-older="loadingOlder"
          @load-older="loadOlderMessages"
        />

        <!-- Input -->
        <MessageInput
          v-if="activeConversation"
          :sending="sending"
          class="shrink-0"
          @send="sendMessage"
        />

        <!-- Empty state (no conversation selected) -->
        <div v-if="!activeConversation" class="flex-1 flex items-center justify-center">
          <div class="text-center">
            <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <MessageSquare class="w-8 h-8 text-gray-400" />
            </div>
            <h3 class="text-lg font-medium text-gray-900 mb-1">Select a conversation</h3>
            <p class="text-sm text-gray-500">Choose a conversation from the list to start messaging</p>
          </div>
        </div>
      </div>

      <!-- Members Panel (right, always visible on lg+) -->
      <MembersPanel
        v-if="activeConversation"
        :participants="activeConversation.participants"
        :subject="activeConversation.subject"
        :level="activeConversation.level"
        :conversation-type="activeConversation.type"
        class="hidden lg:flex lg:flex-col"
      />
    </div>
    </template>
  </DashboardLayout>
</template>
